import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Graph,
  ArrowsClockwise,
  SealCheck,
  CaretRight,
  PushPin,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import {
  artists,
  collaborations,
  collabTypeLabels,
  collabTypeColor,
} from "@/data/mockData";

// Position artists radially
function layoutNodes(list, radius, centerX, centerY) {
  return list.map((a, i) => {
    const angle = (i / list.length) * Math.PI * 2 - Math.PI / 2;
    return {
      ...a,
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  });
}

function curvePath(x1, y1, x2, y2, curviness = 0.25) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dr = Math.sqrt(dx * dx + dy * dy) / curviness;
  return `M ${x1} ${y1} A ${dr} ${dr} 0 0 1 ${x2} ${y2}`;
}

export default function Collaborations() {
  const [activeArtist, setActiveArtist] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [typeFilter, setTypeFilter] = useState("TODOS");

  const nodes = useMemo(
    () => layoutNodes(artists, 280, 400, 400),
    []
  );
  const nodeBySlug = useMemo(() => {
    const m = {};
    nodes.forEach((n) => (m[n.slug] = n));
    return m;
  }, [nodes]);

  const types = ["TODOS", ...new Set(collaborations.map((c) => collabTypeLabels[c.type]))];

  const filteredEdges = useMemo(() => {
    return collaborations.filter((c) => {
      if (typeFilter !== "TODOS" && collabTypeLabels[c.type] !== typeFilter) return false;
      if (activeArtist && c.from !== activeArtist && c.to !== activeArtist) return false;
      return true;
    });
  }, [typeFilter, activeArtist]);

  // Artist degree (number of connections) — used to size nodes
  const degreeBySlug = useMemo(() => {
    const m = {};
    collaborations.forEach((c) => {
      m[c.from] = (m[c.from] || 0) + 1;
      m[c.to] = (m[c.to] || 0) + 1;
    });
    return m;
  }, []);

  const maxDegree = Math.max(...Object.values(degreeBySlug));

  // Stats
  const totalWorks = collaborations.reduce((a, c) => a + c.works, 0);
  const topCollaborator = Object.entries(degreeBySlug).sort(
    (a, b) => b[1] - a[1]
  )[0];
  const topArtist = artists.find((a) => a.slug === topCollaborator[0]);

  return (
    <Layout wide>
      <div data-testid="collabs-page">
        <header className="p-4 md:p-8 border-b border-[#27272A]">
          <div className="flex items-center gap-3 mb-2 font-mono text-[10px] tracking-[0.3em]">
            <Graph size={12} weight="fill" className="text-[#FF3E00]" />
            <span className="text-[#FF3E00]">MAPA DEL ECOSISTEMA · VIVO</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter uppercase leading-none">
            Colaboraciones
            <br />
            <span className="text-[#FF3E00]">cruzadas</span>.
          </h1>
          <p className="font-mono text-sm text-[#A1A1AA] mt-5 max-w-2xl leading-relaxed">
            // CADA LÍNEA ES UNA OBRA QUE EXISTE PORQUE DOS ARTISTAS SE
            ENCONTRARON. MUEVE EL CURSOR SOBRE LAS CONEXIONES Y DESCUBRE QUIÉN
            HIZO QUÉ CON QUIÉN.
          </p>

          <div className="mt-8 grid grid-cols-3 max-w-xl border border-[#27272A]">
            <StatCol n={collaborations.length} label="COLABS" />
            <StatCol n={totalWorks} label="OBRAS CONJUNTAS" divider />
            <StatCol n={topArtist.name.split(" ")[0]} label="MÁS CONECTADO" divider color="#FF3E00" />
          </div>
        </header>

        {/* Filter row */}
        <div className="sticky top-0 z-10 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A] p-3 md:p-4 flex items-center gap-2 overflow-x-auto no-scrollbar">
          {types.map((t) => (
            <button
              key={t}
              data-testid={`collab-type-${t.toLowerCase().replace(/\s+/g, "-").replace(/[+·]/g, "")}`}
              onClick={() => setTypeFilter(t)}
              className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                typeFilter === t
                  ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                  : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5]"
              }`}
            >
              {t}
            </button>
          ))}
          <button
            onClick={() => {
              setTypeFilter("TODOS");
              setActiveArtist(null);
            }}
            className="ml-auto shrink-0 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white px-2 py-2"
          >
            <ArrowsClockwise size={12} /> Reset
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px]">
          {/* Graph canvas */}
          <section className="relative border-b lg:border-b-0 lg:border-r border-[#27272A] bg-[#050505] overflow-hidden">
            <div className="relative w-full" style={{ aspectRatio: "1 / 1", maxHeight: "820px" }}>
              <svg
                viewBox="0 0 800 800"
                className="absolute inset-0 w-full h-full"
                data-testid="collab-graph"
              >
                {/* Concentric rings */}
                <g opacity="0.15">
                  {[120, 200, 280].map((r) => (
                    <circle
                      key={r}
                      cx="400"
                      cy="400"
                      r={r}
                      fill="none"
                      stroke="#27272A"
                      strokeDasharray="2 6"
                    />
                  ))}
                </g>

                {/* Edges */}
                {filteredEdges.map((c) => {
                  const a = nodeBySlug[c.from];
                  const b = nodeBySlug[c.to];
                  if (!a || !b) return null;
                  const color = collabTypeColor[c.type] || "#FF3E00";
                  const isHovered = hoveredEdge === c.id;
                  return (
                    <g key={c.id}>
                      <path
                        d={curvePath(a.x, a.y, b.x, b.y, 0.3 + (c.works % 3) * 0.1)}
                        stroke={color}
                        strokeWidth={isHovered ? 3 : 1.5}
                        fill="none"
                        opacity={isHovered ? 1 : 0.55}
                        className="cursor-pointer transition-opacity"
                        onMouseEnter={() => setHoveredEdge(c.id)}
                        onMouseLeave={() => setHoveredEdge(null)}
                        data-testid={`edge-${c.id}`}
                      />
                    </g>
                  );
                })}

                {/* Nodes */}
                {nodes.map((n) => {
                  const deg = degreeBySlug[n.slug] || 0;
                  const size = 22 + (deg / maxDegree) * 22;
                  const isActive = activeArtist === n.slug;
                  const isRelated =
                    hoveredEdge &&
                    (filteredEdges.find((e) => e.id === hoveredEdge)?.from === n.slug ||
                      filteredEdges.find((e) => e.id === hoveredEdge)?.to === n.slug);
                  return (
                    <g
                      key={n.slug}
                      data-testid={`node-${n.slug}`}
                      className="cursor-pointer"
                      onClick={() =>
                        setActiveArtist((cur) => (cur === n.slug ? null : n.slug))
                      }
                    >
                      {/* Halo */}
                      {isActive ? (
                        <circle
                          cx={n.x}
                          cy={n.y}
                          r={size + 10}
                          fill="none"
                          stroke="#FF3E00"
                          strokeWidth="1.5"
                          opacity="0.6"
                        >
                          <animate
                            attributeName="r"
                            from={size + 6}
                            to={size + 18}
                            dur="1.6s"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            from="0.7"
                            to="0"
                            dur="1.6s"
                            repeatCount="indefinite"
                          />
                        </circle>
                      ) : null}

                      <defs>
                        <clipPath id={`clip-${n.slug}`}>
                          <circle cx={n.x} cy={n.y} r={size} />
                        </clipPath>
                      </defs>
                      <image
                        href={n.avatar}
                        x={n.x - size}
                        y={n.y - size}
                        width={size * 2}
                        height={size * 2}
                        clipPath={`url(#clip-${n.slug})`}
                        preserveAspectRatio="xMidYMid slice"
                        opacity={isActive || isRelated ? 1 : 0.85}
                      />
                      <circle
                        cx={n.x}
                        cy={n.y}
                        r={size}
                        fill="none"
                        stroke={isActive ? "#FF3E00" : isRelated ? "#F4F4F5" : "#27272A"}
                        strokeWidth={isActive ? 2 : 1}
                      />
                      {/* Label */}
                      <text
                        x={n.x}
                        y={n.y + size + 16}
                        textAnchor="middle"
                        className="font-mono"
                        fontSize="10"
                        fill={isActive ? "#FF3E00" : "#F4F4F5"}
                        letterSpacing="0.12em"
                      >
                        {n.name.toUpperCase()}
                      </text>
                      <text
                        x={n.x}
                        y={n.y + size + 28}
                        textAnchor="middle"
                        className="font-mono"
                        fontSize="8"
                        fill="#A1A1AA"
                        letterSpacing="0.1em"
                      >
                        @{n.handle} · {deg} COLABS
                      </text>
                    </g>
                  );
                })}
              </svg>

              {/* Center legend */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                <div className="font-mono text-[9px] text-[#525252] tracking-[0.3em]">
                  ELARTE / NET
                </div>
                <div className="font-display font-black text-xl tracking-tighter uppercase mt-1">
                  El ecosistema
                </div>
              </div>

              {/* Hovered edge tooltip */}
              {hoveredEdge ? (
                <EdgeTooltip
                  edge={collaborations.find((c) => c.id === hoveredEdge)}
                />
              ) : null}
            </div>
          </section>

          {/* Sidebar: details */}
          <aside className="p-4 md:p-5 space-y-5 max-h-[820px] lg:overflow-auto">
            {activeArtist ? (
              <ActivePanel
                slug={activeArtist}
                onClose={() => setActiveArtist(null)}
              />
            ) : (
              <>
                {/* Legend */}
                <div className="border border-[#27272A]">
                  <header className="p-3 border-b border-[#27272A]">
                    <h3 className="font-display font-bold uppercase tracking-tight text-sm">
                      Leyenda
                    </h3>
                  </header>
                  <ul className="p-3 space-y-2">
                    {Object.entries(collabTypeLabels).map(([k, label]) => (
                      <li key={k} className="flex items-center gap-2">
                        <span
                          className="h-0.5 w-8"
                          style={{ background: collabTypeColor[k] }}
                        />
                        <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider uppercase">
                          {label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Instructions */}
                <div className="border border-dashed border-[#27272A] p-4 bg-[#0a0a0a]">
                  <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.22em] mb-2">
                    <PushPin size={11} weight="fill" className="inline mr-1" />
                    TIP
                  </div>
                  <p className="font-mono text-[11px] text-[#E5E5E5] tracking-wider leading-relaxed">
                    CLIC EN UN ARTISTA PARA VER TODAS SUS COLABS. PASA EL CURSOR
                    SOBRE UNA LÍNEA PARA VER LA OBRA CONJUNTA.
                  </p>
                </div>

                {/* Top collaborators */}
                <div className="border border-[#27272A]">
                  <header className="p-3 border-b border-[#27272A]">
                    <h3 className="font-display font-bold uppercase tracking-tight text-sm">
                      Top conectados
                    </h3>
                  </header>
                  <ul>
                    {Object.entries(degreeBySlug)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([slug, n], i) => {
                        const a = artists.find((x) => x.slug === slug);
                        if (!a) return null;
                        return (
                          <li
                            key={slug}
                            className="flex items-center gap-3 p-3 border-b border-[#27272A] last:border-b-0 hover:bg-[#0a0a0a] cursor-pointer"
                            onClick={() => setActiveArtist(slug)}
                          >
                            <span className="font-mono text-[10px] text-[#A1A1AA] w-5">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <img
                              src={a.avatar}
                              alt={a.name}
                              className="h-8 w-8 object-cover border border-[#27272A] grayscale"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-bold text-sm truncate flex items-center gap-1">
                                {a.name}
                                {a.verified ? (
                                  <SealCheck size={10} weight="fill" className="text-[#FF3E00] shrink-0" />
                                ) : null}
                              </div>
                              <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider">
                                {n} conexiones
                              </div>
                            </div>
                            <CaretRight size={12} className="text-[#A1A1AA]" />
                          </li>
                        );
                      })}
                  </ul>
                </div>
              </>
            )}
          </aside>
        </div>

        {/* All collabs list (below) */}
        <section className="p-4 md:p-6 border-t border-[#27272A]">
          <h2 className="font-display font-black uppercase tracking-tight text-xl mb-4">
            Todas las colaboraciones
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filteredEdges.map((c) => {
              const a = artists.find((x) => x.slug === c.from);
              const b = artists.find((x) => x.slug === c.to);
              const color = collabTypeColor[c.type];
              return (
                <li
                  key={c.id}
                  className="flex items-center gap-3 p-3 border border-[#27272A] bg-[#0a0a0a]"
                >
                  <div className="flex items-center -space-x-2 shrink-0">
                    <img
                      src={a.avatar}
                      alt={a.name}
                      className="h-9 w-9 object-cover border-2 border-[#050505]"
                    />
                    <img
                      src={b.avatar}
                      alt={b.name}
                      className="h-9 w-9 object-cover border-2 border-[#050505]"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">
                      {a.name} × {b.name}
                    </div>
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
                      {c.label} · {c.year}
                    </div>
                  </div>
                  <span
                    className="font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 shrink-0"
                    style={{ background: color, color: "#050505" }}
                  >
                    {collabTypeLabels[c.type]}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </Layout>
  );
}

function StatCol({ n, label, divider, color }) {
  return (
    <div
      className={`p-4 text-center ${
        divider ? "border-l border-[#27272A]" : ""
      }`}
    >
      <div
        className="font-display font-black text-2xl md:text-3xl tracking-tight truncate"
        style={color ? { color } : {}}
      >
        {n}
      </div>
      <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mt-1">
        {label}
      </div>
    </div>
  );
}

function EdgeTooltip({ edge }) {
  const fromArt = artists.find((a) => a.slug === edge.from);
  const toArt = artists.find((a) => a.slug === edge.to);
  const color = collabTypeColor[edge.type];
  return (
    <div
      className="absolute top-4 left-4 bg-[#0a0a0a] border border-[#27272A] p-3 max-w-xs"
      style={{ borderColor: color }}
    >
      <div
        className="font-mono text-[9px] uppercase tracking-[0.22em] mb-2"
        style={{ color }}
      >
        {collabTypeLabels[edge.type]} · {edge.year}
      </div>
      <div className="font-display font-bold text-sm">
        {fromArt.name} × {toArt.name}
      </div>
      <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-1">
        {edge.label}
      </div>
      <div className="font-mono text-[10px] text-[#FF3E00] tracking-wider mt-2">
        {edge.works} {edge.works === 1 ? "obra conjunta" : "obras conjuntas"}
      </div>
    </div>
  );
}

function ActivePanel({ slug, onClose }) {
  const a = artists.find((x) => x.slug === slug);
  const collabs = collaborations.filter(
    (c) => c.from === slug || c.to === slug
  );
  return (
    <div className="border border-[#FF3E00] bg-[#FF3E00]/5">
      <header className="p-4 flex items-center gap-3 border-b border-[#FF3E00]/40">
        <img
          src={a.avatar}
          alt={a.name}
          className="h-14 w-14 object-cover border border-[#FF3E00]"
        />
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg tracking-tight flex items-center gap-1">
            {a.name}
            {a.verified ? (
              <SealCheck size={14} weight="fill" className="text-[#FF3E00]" />
            ) : null}
          </div>
          <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
            @{a.handle} · {collabs.length} colabs
          </div>
        </div>
        <button
          onClick={onClose}
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white"
        >
          ✕
        </button>
      </header>
      <div className="p-3">
        <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-2">
          // COLABS ACTIVAS
        </div>
        <ul className="space-y-2">
          {collabs.map((c) => {
            const other = c.from === slug ? c.to : c.from;
            const otherArt = artists.find((x) => x.slug === other);
            const color = collabTypeColor[c.type];
            return (
              <li
                key={c.id}
                className="flex items-center gap-2 p-2 border border-[#27272A] bg-[#050505]"
              >
                <img
                  src={otherArt.avatar}
                  alt={otherArt.name}
                  className="h-9 w-9 object-cover border border-[#27272A]"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-xs truncate">
                    {otherArt.name}
                  </div>
                  <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider truncate">
                    {c.label} · {c.year}
                  </div>
                </div>
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ background: color }}
                />
              </li>
            );
          })}
        </ul>
      </div>
      <div className="p-3 border-t border-[#FF3E00]/40">
        <Link
          to={`/artista/${slug}`}
          className="block text-center font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FF3E00] text-white py-2 hover:bg-white"
        >
          Ir al perfil completo →
        </Link>
      </div>
    </div>
  );
}
